/**
 * Runtime Configuration
 *
 * Supports configuration that can be injected at server startup time,
 * allowing the same build artifact to be deployed with different configurations.
 *
 * Configuration is injected via window.__APP_CONFIG__ by the server before
 * serving index.html (typically by nginx or a docker entrypoint script).
 */

/**
 * Application configuration that can be set at runtime
 */
export interface AppConfig {
  /**
   * Base URL for API requests
   * - Cloud deployments: Full URL like "https://api-staging-dot-dc-classifier.appspot.com"
   * - On-prem deployments: Relative path like "/api" (nginx proxies to backend)
   */
  apiUrl?: string

  /**
   * Optional: Environment identifier for debugging
   */
  environment?: string
}

/**
 * Gets the runtime configuration injected by the server
 *
 * @returns Runtime configuration object, or empty object if not injected
 */
export function getRuntimeConfig(): AppConfig {
  if (typeof window === 'undefined') {
    // Server-side rendering or tests
    return {}
  }

  return window.__APP_CONFIG__ || {}
}

/**
 * Global type declarations for runtime configuration
 */
declare global {
  interface Window {
    /**
     * Runtime configuration injected by server at startup
     * Injected by modifying index.html before serving:
     *   <script>window.__APP_CONFIG__ = {apiUrl: "/api"}</script>
     */
    __APP_CONFIG__?: AppConfig
  }
}

// Export an empty object to make this a module
export {}
